import React from "react"

import { Separator } from "@/components/separator"
import { cn } from "@/domains/library"

type DivProps = React.InputHTMLAttributes<HTMLDivElement>
type SpanProps = React.InputHTMLAttributes<HTMLSpanElement>
type HeadingProps = React.InputHTMLAttributes<HTMLHeadingElement>
type ParagraphProps = React.InputHTMLAttributes<HTMLParagraphElement>

const CardContainer = React.forwardRef<HTMLInputElement, DivProps>(({ className, children, ...props }, ref) => {
  return (
    <div
      className={cn("flex w-full flex-col gap-6 rounded-xl border border-p400-light bg-p400 p-4 lg:p-6", className)}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  )
})

CardContainer.displayName = "CardContainer"

const CardHeader = React.forwardRef<HTMLDivElement, DivProps>(({ className, children, ...props }, ref) => {
  return (
    <div className={cn("flex gap-4", className)} ref={ref} {...props}>
      {children}
    </div>
  )
})

CardHeader.displayName = "CardHeader"

const CardHeaderIconContainer = React.forwardRef<HTMLSpanElement, SpanProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        className={cn(
          "relative flex h-fit w-fit items-center justify-center rounded-lg p-2.5",
          "border border-p300-light",
          // "bg-p300",
          className,
        )}
        style={{
          boxShadow: "inset 0 0 6px 3px hsla(228,39%,40%,0.05)",
          ...(props.style || {}),
        }}
        ref={ref}
        {...props}
      >
        {children}
      </span>
    )
  },
)

CardHeaderIconContainer.displayName = "CardHeaderIconContainer"

const CardHeaderTitle = React.forwardRef<HTMLHeadingElement, HeadingProps>(({ className, children, ...props }, ref) => {
  return (
    <h1 className={cn("text-md font-medium text-t100", className)} ref={ref} {...props}>
      {children}
    </h1>
  )
})

CardHeaderTitle.displayName = "CardHeaderTitle"

const CardHeaderDescription = React.forwardRef<HTMLParagraphElement, ParagraphProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div className={cn("text-sm text-t200", className)} ref={ref} {...props}>
        {children}
      </div>
    )
  },
)

CardHeaderDescription.displayName = "CardHeaderDescription"

const CardSeparator: React.FC<DivProps> = ({ className }) => {
  return (
    <Separator
      className={cn(
        "relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md  after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']",
        className,
      )}
      decorative
    />
  )
}

CardSeparator.displayName = "CardSeparator"

const CardButtons = React.forwardRef<HTMLDivElement, DivProps>(({ className, children, ...props }, ref) => {
  return (
    <div className={cn("flex justify-end", className)} ref={ref} {...props}>
      {children}
    </div>
  )
})

CardButtons.displayName = "CardButtons"

export const Card = {
  Container: CardContainer,
  Header: CardHeader,
  HeaderIconContainer: CardHeaderIconContainer,
  HeaderTitle: CardHeaderTitle,
  HeaderDescription: CardHeaderDescription,
  Separator: CardSeparator,
  Buttons: CardButtons,
}
